import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { IntroSlide } from "./partials/IntroSlide.component";
import { PhoneSlideComponent } from "./partials/PhoneSlide/PhoneSlide.component";
import styles from "./Phone.module.scss";
import { usePhone } from "../../contexts/phone/phone.hook";
import { PhoneSlide } from "../../contexts/phone/Phone.types";

export const Phone = ({ phoneSlides }: { phoneSlides: Array<PhoneSlide> }) => {
  const { isPhoneOpen, setIsPhoneOpen } = usePhone();
  const containerRef = useRef<HTMLDivElement>(null);
  const [slides, setSlides] = useState<Array<PhoneSlide>>(phoneSlides);
  const [activeSlide, setActiveSlide] = useState<string | "introSlide">(
    "introSlide"
  );
  const [openingAnimationComplete, setOpeningAnimationComplete] =
    useState<boolean>(false);
  const [splashScreenComplete, setSplashScreenComplete] =
    useState<boolean>(false);
  const secondSlideRef = useRef<HTMLDivElement | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);

  const phoneVariants = {
    hidden: { y: "125vh", x: "-50%" },
    visible: { y: "0", x: "-50%" },
  };

  const handleOpeningAnimationComplete = () => {
    if (!isPhoneOpen) return;

    setOpeningAnimationComplete(true);
  };

  const handleSplashScreenComplete = () => {
    if (!containerRef.current) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (secondSlideRef.current && containerRef.current) {
          containerRef.current.scrollTo({
            top: secondSlideRef.current.offsetTop,
            behavior: "smooth",
          });
        }
      }, 0);
    });

    setTimeout(() => {
      setSplashScreenComplete(true);
    }, 1000);
  };

  // On open and close
  useEffect(() => {
    if (containerRef.current && isPhoneOpen && !splashScreenComplete) {
      setTimeout(() => {
        containerRef.current?.scrollTo({ top: 0, behavior: "auto" });
        setIsReady(true);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhoneOpen, containerRef.current, splashScreenComplete]);

  // Handle splash screen always on phone open
  useEffect(() => {
    if (!isPhoneOpen || !isReady) return;

    const timeout = setTimeout(() => {
      handleSplashScreenComplete();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [isPhoneOpen, isReady]);

  useEffect(() => {
    if (!slides?.length || !activeSlide || !splashScreenComplete) return;

    const activeIndex = slides.findIndex(
      (slide, index) => `${slide.asset}-${index}` === activeSlide
    );

    // If 2 slides away from the end, duplicate slides
    if (activeIndex >= slides.length - 2) {
      setSlides((prevSlides) => [...prevSlides, ...phoneSlides]);
    }
  }, [activeSlide, slides, phoneSlides, splashScreenComplete]);

  useEffect(() => {
    if (!isPhoneOpen) {
      setActiveSlide("introSlide");
      setOpeningAnimationComplete(false);
      setSplashScreenComplete(false);
      setIsReady(false);
    }
  }, [isPhoneOpen]);

  useEffect(() => {
    if (splashScreenComplete && !isPhoneOpen) {
      setSplashScreenComplete(false);
    }
  }, [isPhoneOpen, splashScreenComplete]);

  useEffect(() => {
    if (
      !isPhoneOpen ||
      !slides.length ||
      !containerRef?.current ||
      !splashScreenComplete
    )
      return;

    const slideElements = Array.from(
      containerRef.current.children
    ) as HTMLElement[];

    const config = {
      root: containerRef.current,
      rootMargin: "-50% 0% -50% 0%",
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const slideId = entry.target.getAttribute("data-slide-id");
        if (entry.isIntersecting && slideId) {
          setActiveSlide(slideId);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, config);
    slideElements.forEach((slide) => observer.observe(slide));

    return () => {
      slideElements.forEach((slide) => observer.unobserve(slide));
      observer.disconnect();
    };
  }, [slides, isPhoneOpen, splashScreenComplete]);

  if (!slides) return null;

  return (
    <AnimatePresence>
      {isPhoneOpen && (
        <>
          {/* Background Blur */}
          <motion.button
            initial={{ backdropFilter: "blur(0px)" }}
            exit={{ backdropFilter: "blur(0px)" }}
            animate={{ backdropFilter: "blur(10px)" }}
            transition={{ type: "tween", duration: 0.5 }}
            className={styles.backgroundBlur}
            onClick={() => {
              setIsPhoneOpen(false);
            }}
            aria-label="Close phone"
          />

          {/* Phone Container */}
          <motion.div
            key={isPhoneOpen ? "open" : "closed"}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ type: "tween", duration: 0.1 }}
            variants={phoneVariants}
            className={styles.phoneContainer}
            onAnimationComplete={handleOpeningAnimationComplete}
          >
            <div
              className={styles.phoneSlidesContainer}
              ref={containerRef}
              style={{
                pointerEvents: splashScreenComplete ? "all" : "none",
                overflowY: splashScreenComplete ? "scroll" : "hidden",
              }}
            >
              {/* Intro Slide */}
              <IntroSlide
                openingAnimationComplete={openingAnimationComplete}
                splashScreenComplete={splashScreenComplete}
                isActive={activeSlide === "introSlide"}
              />

              {isReady &&
                slides.map((slide, index) => (
                  <PhoneSlideComponent
                    ref={index === 0 ? secondSlideRef : undefined}
                    index={index}
                    key={`${slide.asset}-${index}`}
                    dataKey={`${slide.asset}-${index}`}
                    asset={slide.asset}
                    type={slide.type}
                    fallback={slide.fallback}
                    profileData={slide.profileData}
                    isActive={
                      splashScreenComplete &&
                      activeSlide === `${slide.asset}-${index}`
                    }
                  />
                ))}
            </div>

            <img
              src={"/SH_HAND_2560X1440.png"}
              alt=""
              className={styles.handImage}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
